import { type FileRecord } from "@/shared/domain/files";
import { ingestPhase, readVisualizationAsset } from "@/shared/domain/urdf";

/**
 * Whether a listed file can be turned into a model right now, and if not, why.
 *
 * The reason is shown on the row rather than used to hide it. A user who knows
 * `robot.urdf` is in the dataset and cannot find it in the picker has been lied
 * to; one who finds it greyed out reading "Converting…" has been told something
 * true and actionable.
 */
export interface ModelReadiness {
  /** Whether picking this row can yield a model. */
  selectable: boolean;
  /**
   * What to say instead of the row's size-and-date caption.
   *
   * Only set when the state needs explaining — a ready file keeps the default.
   */
  reason?: string;
}

const READY: ModelReadiness = { selectable: true };

/**
 * The row state for one file in the model picker.
 *
 * Pure, and computed from the record the file query already returned: the
 * status block and `upload_id` are both on it, so every row's state costs
 * nothing beyond the listing itself.
 *
 * ## Why this is not a server-side filter
 *
 * The status half of the test is expressible as a query condition, but the
 * other half is not: "current" means `block.source_upload_id ===
 * record.upload_id`, a column-to-column comparison the query language has no
 * form for. A completed-but-stale URDF passes any filter that can be written,
 * so this pass has to exist regardless — and a server condition on top of it
 * would be a second source of truth for something already recomputed here.
 *
 * ## The states
 *
 * Every one of them is a statement about a conversion, because every row the
 * picker lists is a robot description. A `.gltf` used to short-circuit this as
 * "already a model, nothing to convert"; the picker no longer offers one, so
 * that case is gone rather than kept as an unreachable branch. A glTF reaching
 * here now reads as un-ingested, which is the truthful answer to "does an
 * ingested asset back this file" — it does not.
 *
 * - `preview` — an artifact built from the bytes this file currently holds
 * - `converting` — the worker is running; the artifact is not there yet
 * - `failed` — the run stopped; re-running it is the only way on
 * - `settings` — never ingested, **or** ingested from a since-replaced upload.
 *   Both mean "no artifact this file derives", which is the same non-choice
 *   from the picker's side. Picking one will eventually launch the ingest flow;
 *   until then it says why it cannot be chosen.
 */
export function modelReadiness(record: FileRecord): ModelReadiness {
  switch (ingestPhase(readVisualizationAsset(record), record.upload_id)) {
    case "preview":
      return READY;
    case "converting":
      return { selectable: false, reason: "Converting…" };
    case "failed":
      return { selectable: false, reason: "Conversion failed" };
    case "settings":
      return { selectable: false, reason: "Not ingested yet" };
  }
}
